import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../@/components/ui/dropdown-menu';
import { studentsData } from '@/data/studentData';
import axios from 'axios';
import { BookOpen as BookOpenIcon, Search, Filter, SortDesc, MoreVertical, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { api_endpoint } from '@/constants/constant'
import { apiRequest } from '@/lib/apiRequest'
import { queryKeys } from '@/lib/queryKeys'
import { fetchAllPaginatedItems } from '@/lib/fetchAllPaginatedItems'
import { EmptyState } from '@/components/emptyState'
import { Spinner } from '@/components/spinner';
import EnrollStudents from '@/forms/enrollStudents';
import TableToolbar from '../components/tableToolbar';
import { Badge } from '../../@/components/ui/badge';
import DeleteStudentEnrollment from '../forms/deleteStudentEnrollment';
import DeleteStudentEnrollmentsBulkDialog from '../forms/deleteStudentEnrollmentsBulk';
import DefaultTable from '../components/defaultTable';
import Pagination from '../components/pagination';

function getStudentStatus(student) {
    if (student.completed === true) return "COMPLETED";
    if (student.progress > 0) return "ACTIVE";
    return "NOT STARTED";
}

const getStudentId = (student) =>
    student.user_id ??
    student.id ??
    student._id ??
    `${student.email ?? "student"}-${student.course_id ?? "course"}`;

const formatLabel = (value, fallback = "Unknown") => {
    if (value == null) return fallback;
    const upper = String(value).trim().toUpperCase();
    if (!upper) return fallback;
    return upper
        .replace(/[_-]+/g, " ")
        .toLowerCase()
        .replace(/^./, (c) => c.toUpperCase());
};

function parseStudentsResponse(payload) {
    const root = payload?.students ?? payload;
    const items = root?.data ?? (Array.isArray(root) ? root : []);
    const pagination = root?.pagination ?? payload?.pagination ?? null;
    return {
        data: Array.isArray(items) ? items : [],
        pagination,
    };
}

function normalizePaginationMeta(pagination, fallback = {}) {
    const current_page = Number(pagination?.current_page ?? fallback.current_page ?? 1) || 1;
    const page_size = Number(pagination?.page_size ?? fallback.page_size ?? 10) || 10;
    const total_count = Number(pagination?.total_count ?? fallback.total_count ?? 0) || 0;
    const total_pages = Number(pagination?.total_pages ?? fallback.total_pages ?? 1) || 1;
    const has_next = Boolean(pagination?.has_next ?? (current_page < total_pages));
    const has_previous = Boolean(pagination?.has_previous ?? (current_page > 1));
    return {
        current_page,
        page_size,
        total_count,
        total_pages,
        has_next,
        has_previous,
    };
}



export default function StudentsTab({ course, onSelectedStudentsChange, filterStatus: initialFilterStatus = "all", onEnrolledIdsChange, refreshKey, ...props }) {
    const [searchTerm, setSearchTerm] = useState('')
    const [filterStatus, setFilterStatus] = useState(initialFilterStatus)
    const [sortBy, setSortBy] = useState('name')
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [selectedStudents, setSelectedStudents] = useState([])
    const queryClient = useQueryClient()
    const [students, setStudents] = useState(null)
    const [studentsPagination, setStudentsPagination] = useState(() => normalizePaginationMeta(null, { current_page: 1, page_size: 10, total_count: 0, total_pages: 1 }))
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);

    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteSelectedRows, setBulkDeleteSelectedRows] = useState([]);
    const [bulkDeleteSelectedKeys, setBulkDeleteSelectedKeys] = useState([]);
    const [enrollmentFilter, setEnrollmentFilter] = useState('all')
    const [progressFilter, setProgressFilter] = useState('all')
    const [secondSort, setSecondSort] = useState('all')
    const { getToken, isLoaded } = useAuth()
    const courseId = course?.id

    const [pendingFilterStatus, setPendingFilterStatus] = useState(filterStatus);
    const [pendingEnrollmentFilter, setPendingEnrollmentFilter] = useState(enrollmentFilter);
    const [pendingProgressFilter, setPendingProgressFilter] = useState(progressFilter);
    const [pendingSortBy, setPendingSortBy] = useState(sortBy);
    const [pendingSecondSort, setPendingSecondSort] = useState(secondSort);



    useEffect(() => { setPendingFilterStatus(filterStatus); }, [filterStatus]);
    useEffect(() => { setPendingEnrollmentFilter(enrollmentFilter); }, [enrollmentFilter]);
    useEffect(() => { setPendingProgressFilter(progressFilter); }, [progressFilter]);
    useEffect(() => { setPendingSortBy(sortBy); }, [sortBy]);
    useEffect(() => { setPendingSecondSort(secondSort); }, [secondSort]);

    // Notify parent component when selected students change
    useEffect(() => {
        if (onSelectedStudentsChange) {
            onSelectedStudentsChange(selectedStudents)
        }
    }, [selectedStudents, onSelectedStudentsChange])

    // format date to readable string
    const formatDate = (v) => {
        if (!v) return '';
        try {
            const d = new Date(v);
            if (isNaN(d.getTime())) return String(v);

            return d.toLocaleString('en-US', {
                month: 'short',   // "Jan"
                day: '2-digit',   // "20"
                year: 'numeric',  // "2025"
                hour: 'numeric',  // "10"
                minute: '2-digit', // "20"
                hour12: true      // "AM/PM"
            }).replace(',', ''); // remove comma between date and time
        } catch {
            return String(v);
        }
    };

    const studentsQuery = useQuery({
        queryKey: queryKeys.course.studentsPage(courseId, currentPage, rowsPerPage),
        enabled: Boolean(isLoaded && courseId),
        queryFn: async ({ signal }) => {
            const payload = await apiRequest(getToken, {
                path: `/course/${courseId}/students`,
                auth: 'required',
                params: {
                    page: currentPage,
                    page_size: rowsPerPage,
                },
                signal,
                retryOnceOn401: true,
            })
            if (import.meta.env.DEV) {
                console.log("Fetched students:", payload)
            }
            return payload
        },
    })

    const enrolledIdsQuery = useQuery({
        queryKey: queryKeys.course.studentsEnrolledIds(courseId),
        enabled: Boolean(isLoaded && courseId && onEnrolledIdsChange),
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const token = await getToken();
            const items = await fetchAllPaginatedItems({
                url: `${api_endpoint}/course/${courseId}/students`,
                token,
                mode: 'page',
                pageSize: 200,
                selectItems: (data) => {
                    const parsed = parseStudentsResponse(data);
                    return parsed.data;
                },
                getItemKey: (item) => String(item?.user_id ?? item?.id ?? item?._id ?? JSON.stringify(item)),
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            const ids = (Array.isArray(items) ? items : [])
                .map((student) => student?.user_id ?? student?.id ?? student?._id)
                .filter(Boolean);
            return Array.from(new Set(ids));
        },
    });

    useEffect(() => {
        const { data, pagination } = parseStudentsResponse(studentsQuery.data)
        const normalizedList = Array.isArray(data)
            ? data.map((student) => ({
                ...student,
                id: getStudentId(student),
            }))
            : []

        setStudents(normalizedList)
        setStudentsPagination(
            normalizePaginationMeta(pagination, {
                current_page: currentPage,
                page_size: rowsPerPage,
                total_count: 0,
                total_pages: 1,
            })
        )
    }, [studentsQuery.data, currentPage, rowsPerPage])

    useEffect(() => {
        if (!courseId) return
        setSelectedStudents([])
        setCurrentPage(1)
    }, [courseId])

    useEffect(() => {
        if (!courseId) return
        if (refreshKey == null) return
        queryClient.invalidateQueries({ queryKey: queryKeys.course.students(courseId) })
        queryClient.invalidateQueries({ queryKey: queryKeys.course.studentsEnrolledIds(courseId) })
    }, [refreshKey, courseId, queryClient])

    useEffect(() => {
        if (!onEnrolledIdsChange) return
        if (Array.isArray(enrolledIdsQuery.data)) {
            onEnrolledIdsChange(enrolledIdsQuery.data)
        }
    }, [enrolledIdsQuery.data, onEnrolledIdsChange])

    const loading = studentsQuery.isLoading || studentsQuery.isFetching
    const error = studentsQuery.error
        ? (studentsQuery.error?.code === 'NO_TOKEN'
            ? 'Not authenticated.'
            : (studentsQuery.error?.response?.data?.detail || 'Failed to load students.'))
        : null

    // Filter students based on search and status
    const filterConfig = [
        {
            label: "Filter by Status",
            value: pendingFilterStatus,
            onChange: setPendingFilterStatus,
            onClear: () => setPendingFilterStatus('all'),
            options: [
                { value: "all", label: "All Status" },
                { value: "ACTIVE", label: "Active" },
                { value: "COMPLETED", label: "Completed" },
                { value: "NOT STARTED", label: "Not Started" },
            ]
        },
        {
            label: "Filter by Enrollment",
            value: pendingEnrollmentFilter,
            onChange: setPendingEnrollmentFilter,
            onClear: () => setPendingEnrollmentFilter('all'),
            options: [
                { value: "all", label: "All Periods" },
                { value: "recent", label: "Last 30 days" },
                { value: "older", label: "Older than 30 days" }
            ]
        },
        {
            label: "Filter by Progress",
            value: pendingProgressFilter,
            onChange: setPendingProgressFilter,
            onClear: () => setPendingProgressFilter('all'),
            options: [
                { value: "all", label: "All Progress" },
                { value: "high", label: "75%+ Progress" },
                { value: "medium", label: "25-74% Progress" },
                { value: "low", label: "0-24% Progress" }
            ]
        }
    ];

    // Sort the filtered students
    const sortConfig = [
        {
            label: "Sort by",
            value: pendingSortBy,
            onChange: setPendingSortBy,
            onClear: () => setPendingSortBy('name'),
            options: [
                { value: "name", label: "Name" },
                { value: "status", label: "Status" },
                { value: "progress", label: "Progress" },
                { value: "enrollmentDate", label: "Enrollment Date" }
            ]
        },
        {
            label: "Order",  // Changed from "Then sort by" to "Order by"
            value: pendingSecondSort,
            onChange: setPendingSecondSort,
            onClear: () => setPendingSecondSort('enrollmentDate'),
            options: [
                { value: "enrollmentDate", label: "Enrollment Date" },
                { value: "progress", label: "Progress" }
            ]
        }
    ];

    // Compute _status for each student
    const sortedStudents = React.useMemo(() => {
        let processedStudents = Array.isArray(students) ? students.map(student => ({
            ...student,
            _status: getStudentStatus(student)
        })) : [];

        // Search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            processedStudents = processedStudents.filter(student =>
                `${student.first_name ?? ""} ${student.last_name ?? ""}`.toLowerCase().includes(term) ||
                (student.email ?? "").toLowerCase().includes(term)
            );
        }

        // Filter
        if (filterStatus && filterStatus !== "all") {
            processedStudents = processedStudents.filter(student =>
                student._status === filterStatus
            );
        }

        if (enrollmentFilter !== "all") {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            processedStudents = processedStudents.filter(student => {
                const enrollmentDate = new Date(student.created_at);
                if (enrollmentFilter === "recent") {
                    return enrollmentDate >= thirtyDaysAgo;
                } else if (enrollmentFilter === "older") {
                    return enrollmentDate < thirtyDaysAgo;
                }
                return true;
            });
        }

        if (progressFilter !== "all") {
            processedStudents = processedStudents.filter(student => {
                const progress = parseFloat(student.progress) || 0;
                if (progressFilter === "high") {
                    return progress >= 0.75;
                } else if (progressFilter === "medium") {
                    return progress >= 0.25 && progress < 0.75;
                } else if (progressFilter === "low") {
                    return progress < 0.25;
                }
                return true;
            });
        }

        const handleSecondarySort = (a, b, sortType) => {
            switch (sortType) {
                case "enrollmentDate":
                    return new Date(b.created_at) - new Date(a.created_at);
                case "email":
                    return (a.email || "").localeCompare(b.email || "");
                case "progress":
                    return (parseFloat(b.progress) || 0) - (parseFloat(a.progress) || 0);
                default:
                    return 0;
            }
        };

        // Sort
        let sortedStudents = [...processedStudents];
        if (sortBy === "name") {
            sortedStudents.sort((a, b) => {
                const nameCompare = (`${a.first_name ?? ""} ${a.last_name ?? ""}`).localeCompare(`${b.first_name ?? ""} ${b.last_name ?? ""}`);
                if (nameCompare !== 0 || secondSort === "all") return nameCompare;
                return handleSecondarySort(a, b, secondSort);
            });
        } else if (sortBy === "status") {
            const statusOrder = { "COMPLETED": 1, "ACTIVE": 2, "NOT STARTED": 3 };
            sortedStudents.sort((a, b) => {
                const statusCompare = (statusOrder[a._status] || 99) - (statusOrder[b._status] || 99);
                if (statusCompare !== 0 || secondSort === "all") return statusCompare;
                return handleSecondarySort(a, b, secondSort);
            });
        } else if (sortBy === "progress") {
            sortedStudents.sort((a, b) => {
                const progressCompare = (parseFloat(b.progress) || 0) - (parseFloat(a.progress) || 0);
                if (progressCompare !== 0 || secondSort === "all") return progressCompare;
                return handleSecondarySort(a, b, secondSort);
            });
        } else if (sortBy === "enrollmentDate") {
            sortedStudents.sort((a, b) => {
                const dateCompare = new Date(b.created_at) - new Date(a.created_at);
                if (dateCompare !== 0 || secondSort === "all") return dateCompare;
                return handleSecondarySort(a, b, secondSort);
            });
        }

        return sortedStudents
    }, [students, searchTerm, filterStatus, enrollmentFilter, progressFilter, sortBy, secondSort]);

    // Handler for Apply Filters
    const handleApplyFilters = () => {
        setFilterStatus(pendingFilterStatus);
        setEnrollmentFilter(pendingEnrollmentFilter);
        setProgressFilter(pendingProgressFilter);
    };

    // Handler for Apply Sort
    const handleApplySort = () => {
        setSortBy(pendingSortBy);
        setSecondSort(pendingSecondSort);
    };

    const tableRows = useMemo(
        () => sortedStudents.map((student) => ({ ...student, row_id: getStudentId(student) })),
        [sortedStudents]
    );

    const pageIds = useMemo(() => tableRows.map((student) => student.row_id), [tableRows]);

    // Toggle individual student selection
    const toggleStudentSelection = useCallback((studentId) => {
        setSelectedStudents((prev) =>
            prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
        );
    }, []);

    // Toggle select all students
    const handleSelectAllOnPage = useCallback(
        (checked) => {
            setSelectedStudents((prev) => {
                if (checked) {
                    return Array.from(new Set([...prev, ...pageIds]));
                }
                return prev.filter((id) => !pageIds.includes(id));
            });
        },
        [pageIds]
    );

    const handleBulkAction = (label, ctx) => {
        const action = String(label ?? '').trim().toLowerCase();

        if (action === 'delete enrollment') {
            const rowKeys = Array.isArray(ctx?.selectedRowKeysOnPage)
                ? ctx.selectedRowKeysOnPage
                : [];
            const rows = Array.isArray(ctx?.selectedRowsOnPage) ? ctx.selectedRowsOnPage : [];

            if (rows.length < 2) return;

            setBulkDeleteSelectedKeys(rowKeys);
            setBulkDeleteSelectedRows(rows);
            setBulkDeleteDialogOpen(true);
        }
    };

    // Check if all visible students are selected
    // const isAllSelected = selectedStudents.length === sortedStudents.length && sortedStudents.length > 0
    // const isSomeSelected = selectedStudents.length > 0

    const columns = [
        {
            key: "checkbox",
            header: null,
        },
        {
            key: "name",
            header: "Student Name",
            render: (student) => (
                <span className="font-medium font-body">
                    {student.first_name} {student.last_name}
                </span>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (student) => (
                (() => {
                    const statusUpper = String(student._status || "").toUpperCase();
                    return (
                <Badge
                    variant="outline"
                    className={
                        statusUpper === "COMPLETED"
                            ? "border-(--color-accent) text-(--color-accent)"
                            : statusUpper === "ACTIVE"
                                ? "border-(--color-primary) text-(--color-primary)"
                                : "border-(--neutral-400) text-(--neutral-500)"
                    }
                >
                    {formatLabel(statusUpper, "Unknown")}
                </Badge>
                    );
                })()
            ),
        },
        {
            key: "created_at",
            header: "Enrolled On",
            render: (student) => (
                <span className="text-(--neutral-700) font-body">
                    {formatDate(student.created_at)}
                </span>
            ),
        },
        {
            key: "progress",
            header: "Progress",
            render: (student) => (
                <span className="text-(--neutral-700) font-body">
                    {typeof student.progress === "number"
                        ? `${Math.round(student.progress * 100)}%`
                        : student.progress
                            ? `${Math.round(Number(student.progress) * 100)}%`
                            : "0%"}
                </span>
            ),
        },
        {
            key: "actions",
            header: "",
            render: (student) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild className="hover:bg-(--surface-panel) rounded-md hover:text-(--color-accent)">
                        <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem
                            className="font-body"
                            onClick={() => {
                                setSelectedEnrollment({
                                    courseId: student.course_id,
                                    userId: student.user_id,
                                    studentName: `${student.first_name} ${student.last_name}`,
                                });
                                setDeleteDialogOpen(true);
                            }}
                        >
                            Delete Enrollment
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    const totalStudents = Number(studentsPagination?.total_count ?? 0) || 0
    const totalPages = Math.max(1, Number(studentsPagination?.total_pages ?? 1) || 1)

    // Get paginated students
    // const startIndex = (currentPage - 1) * rowsPerPage
    // const endIndex = startIndex + rowsPerPage
    // const paginatedStudents = sortedStudents.slice(startIndex, endIndex)

    if (!course?.id) {
    return (
      <EmptyState
        icon={BookOpenIcon}
        title="No Course Selected"
        description="Please select a course to view enrolled students."
      />
    );
  }

    if (loading || students === null) {
        return (
            <EmptyState
                title={<Spinner />}
                description="Please wait while we load the enrolled students for this course."
            />
        )
    }

    if (!loading && !course?.id) {
        return (
            <EmptyState
                icon={BookOpenIcon}
                title="No Course Selected"
                description="Please select a course to view enrolled students."
            />
        )
    }



    if (error) {
        return (
            <EmptyState
                icon={BookOpenIcon}
                title="Error Loading Students"
                description={error}
            />
        )
    }

    if (!loading && students.length === 0) {
        return (
            <EmptyState
                icon={BookOpenIcon}
                title="No Students"
                description="No students enrolled in this course yet."
            />
        )
    }

    return (
        <>
            {/* Search and Filter Controls */}
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={filterConfig}
                sorts={sortConfig}
                onApplyFilters={handleApplyFilters}
                onApplySort={handleApplySort}
            />


            {/* Students Table */}
            <DefaultTable
                columns={columns}
                data={tableRows}
                emptyMessage="No items found."
                selectedItems={selectedStudents}
                onSelectItem={toggleStudentSelection}
                onSelectAll={handleSelectAllOnPage}
                bulkActions={["Delete Enrollment"]}
                onBulkAction={handleBulkAction}
            />

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 20, 50]}
                onRowsPerPageChange={(value) => {
                    setRowsPerPage(value);
                    setCurrentPage(1);
                }}
                onPageChange={(page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)))}
                totalRows={totalStudents}
                selectedCount={selectedStudents.length}
                rowLabel="students"
            />

            <DeleteStudentEnrollment
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                courseId={selectedEnrollment?.courseId}
                userId={selectedEnrollment?.userId}
                studentName={selectedEnrollment?.studentName}
                onDeleted={() => {
                    setDeleteDialogOpen(false);
                    setSelectedEnrollment(null);
                    if (courseId) {
                        queryClient.invalidateQueries({ queryKey: queryKeys.course.students(courseId) })
                        queryClient.invalidateQueries({ queryKey: queryKeys.course.studentsEnrolledIds(courseId) })
                    }
                    studentsQuery.refetch?.()
                }}
            />

            <DeleteStudentEnrollmentsBulkDialog
                open={bulkDeleteDialogOpen}
                onOpenChange={setBulkDeleteDialogOpen}
                courseId={courseId}
                selectedStudents={bulkDeleteSelectedRows}
                onDeleted={() => {
                    setBulkDeleteDialogOpen(false);
                    setBulkDeleteSelectedRows([]);

                    // Clear selected row keys from selection state.
                    setSelectedStudents((prev) =>
                        Array.isArray(prev)
                            ? prev.filter((key) => !bulkDeleteSelectedKeys.includes(key))
                            : []
                    );
                    setBulkDeleteSelectedKeys([]);

                    if (courseId) {
                        queryClient.invalidateQueries({ queryKey: queryKeys.course.students(courseId) })
                        queryClient.invalidateQueries({ queryKey: queryKeys.course.studentsEnrolledIds(courseId) })
                    }
                    studentsQuery.refetch?.()
                }}
            />
        </>
    )
}